import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Configured axios instance that always sends httpOnly cookies.
 * Tokens are never stored in localStorage — auth relies on
 * secure httpOnly cookies set by the backend, with an in-memory
 * Bearer fallback for iframe contexts that block 3rd-party cookies.
 *
 * Timeout: 120s — AI cycle generation can take 30–90s.
 *
 * Retry policy: idempotent requests + AI generation endpoints automatically
 * retry once on transient 502/503/504 (ingress/proxy hiccups) and on
 * network errors. Avoids spurious failures during AI cycle generation.
 */
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 120000,
});

// Endpoints that are safe to retry on transient upstream errors even when POST.
// Note: /generate-cycle is intentionally NOT retried — it is now an async job
// with its own poll loop (see LandingPage.jsx). Retrying would enqueue duplicates.
const RETRYABLE_POST_PATHS = [
  '/generate-custom-cycle',
  '/promo/validate',
  '/subscription/status',
];

const isRetryableTransient = (error) => {
  const status = error.response?.status;
  // Network error or ingress/proxy errors
  if (!error.response) return true;
  return status === 502 || status === 503 || status === 504;
};

const shouldRetry = (config, error) => {
  if (!config || config.__retried) return false;
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'post') return isRetryableTransient(error);
  // For POST, only allow retry for explicitly whitelisted endpoints
  const url = config.url || '';
  return (
    RETRYABLE_POST_PATHS.some((p) => url.includes(p)) && isRetryableTransient(error)
  );
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config } = error;
    if (shouldRetry(config, error)) {
      config.__retried = true;
      // 1.5s backoff for ingress to recover
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return api.request(config);
    }
    return Promise.reject(error);
  }
);

export default api;
